// src/pages/plantation/setara-urea/AkumUreaBibitKecil.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Stack,
    Snackbar,
    Chip,
    Tabs,
    Tab,
    IconButton,
    Collapse,
    TableContainer,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    Slide,
    InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "../../../components/css/ded-style.css";
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import LineChart dari MUI X Charts
import { LineChart } from "@mui/x-charts/LineChart";

// Import icons
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// Import fungsi hitung umur
import { calculateUmur } from "../formula/hitung-umur";

// Import fungsi perhitungan untuk menghitung Setara Urea dan K2SO4
import { calculateAllValues } from "../formula/Calculation";

// Transition untuk Dialog Full Screen
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function untuk membulatkan Standard SBT
const roundStandardSBT = (value) => {
    if (!value || value.toString().trim() === "" || value === "-") {
        return "-";
    }

    const strValue = value.toString().trim();

    // Ganti koma dengan titik untuk parsing
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return strValue;
    }

    // Math.round untuk membulatkan ke bilangan bulat terdekat
    return Math.round(numValue).toString();
};

// Helper function untuk membulatkan Setara Urea (tanpa desimal)
const roundSetaraUrea = (value) => {
    if (!value || value.toString().trim() === "" || value === "-") {
        return "-";
    }

    const strValue = value.toString().trim();

    // Ganti koma dengan titik untuk parsing
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return strValue;
    }

    // Math.round untuk membulatkan ke bilangan bulat terdekat
    return Math.round(numValue).toString();
};

// Helper function untuk membulatkan Setara K2SO4 (tanpa desimal)
const roundSetaraK2SO4 = (value) => {
    if (!value || value.toString().trim() === "" || value === "-") {
        return "-";
    }

    const strValue = value.toString().trim();

    // Ganti koma dengan titik untuk parsing
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return strValue;
    }

    // Math.round untuk membulatkan ke bilangan bulat terdekat
    return Math.round(numValue).toString();
};

// Fungsi untuk menghitung N: Setara Urea * 46%
const calculateNFromSetaraUrea = (setaraUrea) => {
    if (!setaraUrea || setaraUrea === "-" || setaraUrea.toString().trim() === "") {
        return "-";
    }

    const strValue = setaraUrea.toString().trim();
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return "-";
    }

    const nValue = numValue * 0.46;
    // Bulatkan menggunakan Math.round
    return Math.round(nValue).toString();
};

// Fungsi untuk menghitung K: Setara K2SO4 * 50%
const calculateKFromSetaraK2SO4 = (setaraK2SO4) => {
    if (!setaraK2SO4 || setaraK2SO4 === "-" || setaraK2SO4.toString().trim() === "") {
        return "-";
    }

    const strValue = setaraK2SO4.toString().trim();
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return "-";
    }

    const kValue = numValue * 0.5;
    // Bulatkan menggunakan Math.round
    return Math.round(kValue).toString();
};

// Fungsi untuk menghitung K/N: N / K
const calculateKNFromNK = (kValue, nValue) => {
    if (kValue === "-" || nValue === "-" || kValue.toString().trim() === "" || nValue.toString().trim() === "") {
        return "-";
    }

    // Parse K value
    const kStr = kValue.toString().trim();
    const kNormalized = kStr.replace(",", ".");
    const kNum = parseFloat(kNormalized);

    // Parse N value
    const nStr = nValue.toString().trim();
    const nNormalized = nStr.replace(",", ".");
    const nNum = parseFloat(nNormalized);

    if (isNaN(kNum) || isNaN(nNum) || nNum === 0) {
        return "-";
    }

    // Hitung K/N: K dibagi N
    const knRatio = kNum / nNum;

    // Format dengan 2 digit di belakang koma
    return knRatio.toFixed(2);
};

// Fungsi untuk mengelompokkan umur berdasarkan ketentuan
const getUmurGroup = (umurDecimal) => {
    if (typeof umurDecimal !== "number" || isNaN(umurDecimal)) {
        return null;
    }

    // PERBAIKAN: Kelompokkan berdasarkan integer (bulat ke bawah)
    // Contoh: 2.73 → kelompok 2, 3.10 → kelompok 3, dst.
    return Math.floor(umurDecimal);
};

// PERBAIKAN: Helper function untuk ekstrak jenis dan kelas bibit dari string
// Format: NFMD2CK4 -> karakter ke-6 (0-based: 5) = Jenis Bibit, karakter ke-7 (0-based: 6) = Kelas Bibit
const extractJenisKelasBibit = (bibitString) => {
    if (!bibitString || typeof bibitString !== 'string') {
        console.log("[extractJenisKelasBibit] Input tidak valid:", bibitString);
        return { jenis: "", kelas: "", raw: bibitString || "" };
    }

    const str = bibitString.trim();
    console.log(`[extractJenisKelasBibit] Memproses: "${str}" (panjang: ${str.length})`);
    
    let jenis = "";
    let kelas = "";
    
    // Cek apakah string cukup panjang untuk mengekstrak karakter ke-6 dan ke-7
    if (str.length >= 7) {
        // Karakter ke-6 (index 5 dalam 0-based)
        jenis = str.charAt(5);
        // Karakter ke-7 (index 6 dalam 0-based)
        kelas = str.charAt(6);
        
        console.log(`[extractJenisKelasBibit] Hasil - Jenis: "${jenis}", Kelas: "${kelas}"`);
    } else {
        console.log(`[extractJenisKelasBibit] String terlalu pendek: ${str.length} karakter`);
        
        // Fallback: coba cari pola C atau K di dalam string
        if (str.includes("C") || str.includes("K")) {
            const cIndex = str.indexOf("C");
            const kIndex = str.indexOf("K");
            
            if (cIndex >= 0) {
                jenis = "C";
                if (cIndex + 1 < str.length) {
                    kelas = str.charAt(cIndex + 1);
                }
            } else if (kIndex >= 0) {
                kelas = "K";
                if (kIndex - 1 >= 0) {
                    jenis = str.charAt(kIndex - 1);
                }
            }
        }
    }
    
    return { jenis, kelas, raw: str };
};

// Fungsi untuk parse nilai numerik dengan aman
const parseNumericValue = (value) => {
    if (!value && value !== 0) return 0;

    // Jika sudah number, return langsung
    if (typeof value === "number") return value;

    // Jika string, coba parse
    const strValue = value.toString().trim();
    if (strValue === "" || strValue === "-") return 0;

    // Ganti koma dengan titik untuk parsing
    const normalized = strValue.replace(",", ".");
    const numValue = parseFloat(normalized);

    return isNaN(numValue) ? 0 : numValue;
};

// Fungsi untuk membulatkan angka tanpa desimal (untuk Detail Lokasi)
const roundNumberNoDecimal = (value) => {
    if (!value || value === "-" || value.toString().trim() === "") {
        return "-";
    }

    const strValue = value.toString().trim();
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return strValue;
    }

    // Bulatkan ke bilangan bulat terdekat
    return Math.round(numValue).toString();
};

// Komponen untuk menampilkan detail lokasi per kelompok umur
const DetailLokasiTable = ({ detailData, detailSearchQuery }) => {
    const getFilteredDetailData = (umurGroup) => {
        if (!detailData[umurGroup] || !Array.isArray(detailData[umurGroup])) {
            return [];
        }
        
        if (!detailSearchQuery.trim()) {
            return detailData[umurGroup];
        }
        
        const query = detailSearchQuery.toLowerCase().trim();
        return detailData[umurGroup].filter(item => 
            item.id_lokasi.toLowerCase().includes(query) ||
            item.lokasi_nama.toLowerCase().includes(query) ||
            item.umur_decimal.toString().includes(query) ||
            item.setara_urea.toString().includes(query) ||
            item.setara_k2so4.toString().includes(query) ||
            item.bibit.toLowerCase().includes(query)
        );
    };

    return (
        <Box>
            {/* Tampilkan semua detail data per kelompok umur */}
            {Object.keys(detailData).sort((a, b) => a - b).map((umurGroup) => {
                const items = getFilteredDetailData(umurGroup);
                if (items.length === 0) return null;
                
                return (
                    <Box key={umurGroup} sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography
                                variant="subtitle1"
                                gutterBottom
                                sx={{ 
                                    alignItems: 'center',
                                    color: 'primary.main',
                                    fontWeight: 'bold'
                                }}>
                                Lokasi Umur {umurGroup} bulan
                            </Typography>
                            <Chip 
                                label={`${items.length} lokasi`}
                                size="small"
                                color="primary"
                                variant="filled"
                                sx={{ ml: 2 }}
                            />
                        </Stack>
                        <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Lokasi</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Umur</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Setara Urea</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Setara K2SO4</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Mulai Rawat</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Bibit</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Jenis Bibit</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Kelas Bibit</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Wilayah</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Netto (Ha)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length > 0 ? (
                                        items.map((detail, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{detail.lokasi_nama || detail.id_lokasi}</TableCell>
                                                <TableCell>{detail.umur_decimal}</TableCell>
                                                {/* Gunakan roundNumberNoDecimal untuk menghilangkan desimal */}
                                                <TableCell>{roundNumberNoDecimal(detail.setara_urea)}</TableCell>
                                                <TableCell>{roundNumberNoDecimal(detail.setara_k2so4)}</TableCell>
                                                <TableCell>{detail.mulai_rawat}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={detail.status} 
                                                        size="small" 
                                                        color={detail.status === "NFFC" ? "primary" : "default"}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{detail.bibit}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={detail.bibit_jenis} 
                                                        size="small" 
                                                        color={detail.bibit_jenis === "C" ? "warning" : "default"}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={detail.bibit_kelas} 
                                                        size="small" 
                                                        color={detail.bibit_kelas === "K" ? "error" : "default"}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{detail.wilayah}</TableCell>
                                                <TableCell>{detail.netto}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                Tidak ada data detail untuk kelompok umur ini
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                );
            })}
            
            {Object.keys(detailData).length === 0 && (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                    Tidak ada data detail lokasi yang ditemukan berdasarkan kriteria filter.
                </Typography>
            )}
        </Box>
    );
};

function AkumUreaBibitKecil() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    // State untuk tab
    const [activeTab, setActiveTab] = useState(0);

    // Data untuk chart
    const [chartData, setChartData] = useState({
        xLabels: [],
        uData: [],
        pData: [],
    });

    // State baru untuk menyimpan detail lokasi per kelompok umur
    const [detailData, setDetailData] = useState({});
    
    // State untuk expand/collapse detail per kelompok umur
    const [expandedGroups, setExpandedGroups] = useState({});
    
    // State untuk pencarian detail
    const [detailSearchQuery, setDetailSearchQuery] = useState('');
    
    // State untuk debugging filter
    const [debugInfo, setDebugInfo] = useState({
        totalDataProcessed: 0,
        filteredByStatus: 0,
        filteredByBibit: 0,
        filteredByClosing: 0,
        filteredByUmur: 0,
        finalDataCount: 0,
        bibitErrors: []
    });

    // State untuk dialog full screen Detail Lokasi
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    // Fungsi untuk menghitung interval yAxis
    const getYAxisTicks = (maxValue) => {
        if (maxValue <= 0) return [0, 100, 200, 300];

        // Hitung interval yang bagus (kelipatan 100)
        const interval = 100;
        const maxTick = Math.ceil(maxValue / interval) * interval;
        const ticks = [];

        // Generate ticks: 0, 100, 200, ...
        for (let i = 0; i <= maxTick; i += interval) {
            ticks.push(i);
        }

        return ticks;
    };

    // Fungsi untuk toggle expand/collapse kelompok umur
    const handleToggleExpand = (umurGroup) => {
        setExpandedGroups(prev => ({
            ...prev,
            [umurGroup]: !prev[umurGroup]
        }));
    };

    // Fungsi untuk membuka dialog Detail Lokasi
    const handleOpenDetailDialog = () => {
        setDetailDialogOpen(true);
    };

    // Fungsi untuk menutup dialog Detail Lokasi
    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // 1. Fetch data Standar Per Umur untuk mendapatkan Standard SBT
                // PERUBAHAN: Filter untuk Jenis Bibit = C, Kelas Bibit = K
                const standarUmurRef = ref(database, "db_plantation/tb_standar_per_umur");
                const standarUmurSnapshot = await onValuePromise(standarUmurRef);
                const standarUmurData = standarUmurSnapshot.val() || {};

                // 2. Fetch data Monitor Setara Urea Per Umur untuk data mentah
                const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
                const setaraUreaSnapshot = await onValuePromise(setaraUreaRef);
                const setaraUreaData = setaraUreaSnapshot.val() || {};

                // 3. Fetch data tb_lokasi untuk informasi tambahan
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiData = lokasiSnapshot.val() || {};

                // 4. Fetch data hasil panen untuk tanggal closing
                const hasilPanenRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
                const hasilPanenSnapshot = await onValuePromise(hasilPanenRef);
                const hasilPanenData = hasilPanenSnapshot.val() || {};

                // Konversi lokasiData menjadi map untuk pencarian cepat
                const lokasiMap = {};
                Object.keys(lokasiData).forEach((key) => {
                    const item = lokasiData[key];
                    if (item.id_lokasi) {
                        lokasiMap[item.id_lokasi] = item;
                    }
                });

                // Buat map untuk tanggal closing
                const closingMap = {};
                Object.keys(hasilPanenData).forEach((key) => {
                    const item = hasilPanenData[key];
                    if (
                        item.id_lokasi &&
                        item.tanggal_closing &&
                        item.tanggal_closing.trim() !== "" &&
                        item.tanggal_closing !== "-"
                    ) {
                        closingMap[item.id_lokasi] = item.tanggal_closing;
                    }
                });

                // Proses data Standar Per Umur sesuai kriteria
                // PERUBAHAN: Kriteria: Jenis Bibit = C, Kelas Bibit = K, Standar SBT tidak kosong
                const standarDataProcessed = [];

                Object.keys(standarUmurData).forEach((key) => {
                    const record = standarUmurData[key];

                    // Filter berdasarkan kriteria
                    if (
                        record.jenis_bibit === "C" && // PERUBAHAN: Jenis Bibit = C
                        record.kelas_bibit === "K" && // PERUBAHAN: Kelas Bibit = K
                        record.standar_sbt &&
                        record.standar_sbt.toString().trim() !== ""
                    ) {
                        // Parse umur untuk pengelompokan
                        const umurText = record.umur || "";
                        const umurMatch = umurText.match(/(\d+)/);
                        const umurValue = umurMatch ? parseInt(umurMatch[1]) : 0;

                        // Bulatkan Standard SBT menggunakan Math.round
                        const roundedSBT = roundStandardSBT(record.standar_sbt);

                        standarDataProcessed.push({
                            umur: umurText,
                            umurValue: umurValue,
                            standard_sbt: roundedSBT,
                            // Data lain akan diisi nanti
                            setara_urea_rata: 0,
                            n: "-",
                            setara_k2so4: 0,
                            k: "-",
                            k_n: "-",
                            count_data: 0,
                        });
                    }
                });

                // Urutkan berdasarkan umur
                standarDataProcessed.sort((a, b) => a.umurValue - b.umurValue);

                // Proses data Setara Urea Per Umur untuk menghitung Setara Urea dan K2SO4 dengan kriteria yang ditentukan
                const setaraUreaByUmurGroup = {};
                const setaraK2SO4ByUmurGroup = {};
                const detailDataByUmurGroup = {}; // Untuk menyimpan detail lokasi per kelompok umur
                const umurGroupDebug = {}; // Untuk debugging
                
                // Variabel untuk debugging filter
                let totalDataProcessed = 0;
                let filteredByStatus = 0;
                let filteredByBibit = 0;
                let filteredByClosing = 0;
                let filteredByUmur = 0;
                let finalDataCount = 0;
                const bibitErrors = [];

                Object.keys(setaraUreaData).forEach((key) => {
                    const record = setaraUreaData[key];
                    const idLokasi = record.id_lokasi;

                    if (!idLokasi) return;

                    totalDataProcessed++;

                    // Dapatkan informasi lokasi dari lokasiMap
                    const lokasiInfo = lokasiMap[idLokasi];
                    if (!lokasiInfo) {
                        console.log(`[DEBUG] Lokasi tidak ditemukan di tb_lokasi: ${idLokasi}`);
                        return;
                    }

                    // 1. Filter berdasarkan Status = NFFC
                    if (lokasiInfo.status !== "NFFC") {
                        console.log(`[DEBUG] ${idLokasi}: Bukan NFFC (${lokasiInfo.status})`);
                        return;
                    }
                    filteredByStatus++;

                    // 2. Filter berdasarkan Jenis dan Kelas Bibit
                    // PERUBAHAN: Filter untuk Jenis Bibit = C dan Kelas Bibit = K
                    const bibitValue = lokasiInfo.bibit || "";
                    const { jenis, kelas, raw } = extractJenisKelasBibit(bibitValue);
                    
                    console.log(`[DEBUG] ${idLokasi}: Bibit="${raw}" → Jenis="${jenis}", Kelas="${kelas}"`);
                    
                    // PERUBAHAN: Kriteria sekarang adalah Jenis Bibit = C dan Kelas Bibit = K
                    if (!(jenis === "C" && kelas === "K")) {
                        console.log(`[DEBUG] ${idLokasi}: Tidak memenuhi kriteria bibit C/K (Jenis=${jenis}, Kelas=${kelas})`);
                        bibitErrors.push({
                            id_lokasi: idLokasi,
                            bibit: raw,
                            jenis: jenis,
                            kelas: kelas,
                            expected: "C/K"
                        });
                        return;
                    }
                    filteredByBibit++;

                    // 3. Filter berdasarkan tanggal closing (tidak boleh ada)
                    const hasClosingDate =
                        closingMap[idLokasi] && closingMap[idLokasi].trim() !== "" && closingMap[idLokasi] !== "-";
                    
                    if (hasClosingDate) {
                        console.log(`[DEBUG] ${idLokasi}: Sudah memiliki tanggal closing (${closingMap[idLokasi]})`);
                        return;
                    }
                    filteredByClosing++;

                    // 4. Hitung umur dan filter umur ≥ 1
                    const mulaiRawat = lokasiInfo.mulai_rawat;
                    if (!mulaiRawat) {
                        console.log(`[DEBUG] ${idLokasi}: Tidak memiliki tanggal mulai rawat`);
                        return;
                    }

                    // Hitung umur menggunakan fungsi yang sama dengan MonitorSetaraUreaPerUmur
                    const umurDecimal = calculateUmur(idLokasi, mulaiRawat, hasilPanenData);

                    if (umurDecimal === null || umurDecimal < 1) {
                        console.log(`[DEBUG] ${idLokasi}: Umur tidak valid (< 1 bulan): ${umurDecimal}`);
                        return;
                    }
                    filteredByUmur++;

                    // Kelompokkan umur sesuai ketentuan
                    const umurGroup = getUmurGroup(umurDecimal);

                    if (umurGroup !== null && umurGroup >= 1) {
                        finalDataCount++;
                        
                        // DEBUG: Simpan data untuk verifikasi
                        if (!umurGroupDebug[umurGroup]) {
                            umurGroupDebug[umurGroup] = [];
                        }
                        umurGroupDebug[umurGroup].push({
                            id_lokasi: idLokasi,
                            umur_decimal: umurDecimal,
                            umur_group: umurGroup
                        });

                        // HITUNG SETARA UREA DAN K2SO4 dengan cara yang SAMA PERSIS dengan MonitorSetaraUreaPerUmur
                        // Gunakan fungsi calculateAllValues yang sama
                        const calculatedValues = calculateAllValues({
                            kcl: record.kcl || 0,
                            k2so4: record.k2so4 || 0,
                            za: record.za || 0,
                            dap: record.dap || 0,
                            urea: record.urea || 0,
                        });

                        // Dapatkan nilai setaraUrea dari hasil perhitungan
                        const setaraUreaValue =
                            calculatedValues.setaraUrea !== null
                                ? parseNumericValue(calculatedValues.setaraUrea)
                                : 0;

                        // Dapatkan nilai setaraK2SO4 dari hasil perhitungan
                        const setaraK2SO4Value =
                            calculatedValues.setaraK2SO4 !== null
                                ? parseNumericValue(calculatedValues.setaraK2SO4)
                                : 0;

                        // Proses Setara Urea
                        if (!setaraUreaByUmurGroup[umurGroup]) {
                            setaraUreaByUmurGroup[umurGroup] = {
                                total: setaraUreaValue,
                                count: 1,
                            };
                        } else {
                            setaraUreaByUmurGroup[umurGroup].total += setaraUreaValue;
                            setaraUreaByUmurGroup[umurGroup].count += 1;
                        }

                        // Proses Setara K2SO4
                        if (!setaraK2SO4ByUmurGroup[umurGroup]) {
                            setaraK2SO4ByUmurGroup[umurGroup] = {
                                total: setaraK2SO4Value,
                                count: 1,
                            };
                        } else {
                            setaraK2SO4ByUmurGroup[umurGroup].total += setaraK2SO4Value;
                            setaraK2SO4ByUmurGroup[umurGroup].count += 1;
                        }

                        // Simpan detail data untuk log (nilai asli dengan desimal)
                        if (!detailDataByUmurGroup[umurGroup]) {
                            detailDataByUmurGroup[umurGroup] = [];
                        }
                        detailDataByUmurGroup[umurGroup].push({
                            id_lokasi: idLokasi,
                            lokasi_nama: lokasiInfo.lokasi || idLokasi, // Tambahkan nama lokasi
                            umur_decimal: umurDecimal.toFixed(2),
                            umur_group: umurGroup,
                            setara_urea: setaraUreaValue.toFixed(2), // Simpan dengan desimal untuk ditampilkan nanti
                            setara_k2so4: setaraK2SO4Value.toFixed(2), // Simpan dengan desimal untuk ditampilkan nanti
                            mulai_rawat: lokasiInfo.mulai_rawat || "-",
                            status: lokasiInfo.status || "-",
                            bibit: lokasiInfo.bibit || "-",
                            bibit_jenis: jenis,
                            bibit_kelas: kelas,
                            wilayah: lokasiInfo.wilayah || "-",
                            netto: lokasiInfo.netto || "-",
                            tahun: lokasiInfo.tahun || "-"
                        });
                    }
                });

                // Simpan debug info
                setDebugInfo({
                    totalDataProcessed,
                    filteredByStatus,
                    filteredByBibit,
                    filteredByClosing,
                    filteredByUmur,
                    finalDataCount,
                    bibitErrors: bibitErrors.slice(0, 10) // Simpan hanya 10 error pertama
                });

                // Debug: log hasil pengelompokan umur
                console.log("=== DEBUG PENGELOMPOKAN UMUR (BIBIT KECIL) ===");
                console.log(`Total data diproses: ${totalDataProcessed}`);
                console.log(`Setelah filter NFFC: ${filteredByStatus}`);
                console.log(`Setelah filter bibit C/K: ${filteredByBibit}`);
                console.log(`Setelah filter tanpa closing: ${filteredByClosing}`);
                console.log(`Setelah filter umur ≥ 1: ${filteredByUmur}`);
                console.log(`Data final: ${finalDataCount}`);
                
                Object.keys(umurGroupDebug).sort((a, b) => a - b).forEach((group) => {
                    const items = umurGroupDebug[group];
                    console.log(`Umur Kelompok ${group}: ${items.length} data`);
                    items.slice(0, 5).forEach((item, index) => {
                        console.log(`  ${index + 1}. ID: ${item.id_lokasi}, Umur: ${item.umur_decimal.toFixed(2)} → Kelompok: ${item.umur_group}`);
                    });
                    if (items.length > 5) {
                        console.log(`  ... dan ${items.length - 5} data lainnya`);
                    }
                });
                console.log("==============================================");

                // Debug: log hasil pengelompokan Setara Urea
                console.log("=== HASIL PENGELOMPOKAN SETARA UREA (BIBIT KECIL) ===");
                Object.keys(setaraUreaByUmurGroup).sort((a, b) => a - b).forEach((group) => {
                    const data = setaraUreaByUmurGroup[group];
                    const rataRata = data.total / data.count;
                    console.log(
                        `Umur ${group}: ${data.count} data, total: ${data.total.toFixed(2)}, rata-rata: ${rataRata.toFixed(2)}`
                    );
                });
                console.log("==================================================");

                // Debug: log hasil pengelompokan Setara K2SO4
                console.log("=== HASIL PENGELOMPOKAN SETARA K2SO4 (BIBIT KECIL) ===");
                Object.keys(setaraK2SO4ByUmurGroup).sort((a, b) => a - b).forEach((group) => {
                    const data = setaraK2SO4ByUmurGroup[group];
                    const rataRata = data.total / data.count;
                    console.log(
                        `Umur ${group}: ${data.count} data, total: ${data.total.toFixed(2)}, rata-rata: ${rataRata.toFixed(2)}`
                    );
                });
                console.log("===================================================");

                // Simpan detail data ke state
                setDetailData(detailDataByUmurGroup);

                // Gabungkan data standar dengan rata-rata
                const finalDataList = standarDataProcessed.map((item) => {
                    const umurGroup = item.umurValue;
                    const groupDataUrea = setaraUreaByUmurGroup[umurGroup];
                    const groupDataK2SO4 = setaraK2SO4ByUmurGroup[umurGroup];

                    // Inisialisasi variabel
                    let setaraUreaRata = "-";
                    let setaraK2SO4Rata = "-";
                    let count_data = 0;

                    // Proses Setara Urea
                    if (groupDataUrea && groupDataUrea.count > 0) {
                        const rataRataUrea = groupDataUrea.total / groupDataUrea.count;
                        // Bulatkan Setara Urea menggunakan Math.round
                        setaraUreaRata = roundSetaraUrea(rataRataUrea);
                        count_data = groupDataUrea.count; // Gunakan count dari groupDataUrea
                    }

                    // Proses Setara K2SO4
                    if (groupDataK2SO4 && groupDataK2SO4.count > 0) {
                        const rataRataK2SO4 = groupDataK2SO4.total / groupDataK2SO4.count;
                        // Bulatkan Setara K2SO4 menggunakan Math.round
                        setaraK2SO4Rata = roundSetaraK2SO4(rataRataK2SO4);
                        // Jika count_data belum diisi (tidak ada data urea tapi ada data K2SO4), gunakan count dari K2SO4
                        if (count_data === 0) {
                            count_data = groupDataK2SO4.count;
                        }
                    }

                    // Hitung N: Setara Urea * 46%
                    const nValue = calculateNFromSetaraUrea(setaraUreaRata);

                    // Hitung K: Setara K2SO4 * 50%
                    const kValue = calculateKFromSetaraK2SO4(setaraK2SO4Rata);

                    // Hitung K/N: N / K
                    const knValue = calculateKNFromNK(kValue, nValue);

                    return {
                        ...item,
                        setara_urea_rata: setaraUreaRata,
                        n: nValue,
                        setara_k2so4: setaraK2SO4Rata,
                        k: kValue,
                        k_n: knValue,
                        count_data: count_data,
                    };
                });

                setOriginalDataList(finalDataList);
                setDataList(finalDataList);

                // Siapkan data untuk chart
                const xLabels = finalDataList.map((item) => item.umur);
                const uData = finalDataList.map((item) => {
                    // Konversi Standard SBT ke number untuk chart
                    if (item.standard_sbt && item.standard_sbt !== "-" && item.standard_sbt.toString().trim() !== "") {
                        const strValue = item.standard_sbt.toString().trim().replace(",", ".");
                        const numValue = parseFloat(strValue);
                        return isNaN(numValue) ? 0 : numValue;
                    }
                    return 0;
                });
                const pData = finalDataList.map((item) => {
                    // Konversi Setara Urea ke number untuk chart
                    if (
                        item.setara_urea_rata &&
                        item.setara_urea_rata !== "-" &&
                        item.setara_urea_rata.toString().trim() !== ""
                    ) {
                        const strValue = item.setara_urea_rata.toString().trim().replace(",", ".");
                        const numValue = parseFloat(strValue);
                        return isNaN(numValue) ? 0 : numValue;
                    }
                    return 0;
                });

                setChartData({
                    xLabels,
                    uData,
                    pData,
                });

                console.log("=== SUMMARY DATA AKUMULASI UREA DAN K2SO4 (BIBIT KECIL) ===");
                console.log("Total records:", finalDataList.length);
                let totalDataDigunakan = 0;
                finalDataList.forEach((item) => {
                    if (item.count_data > 0) {
                        totalDataDigunakan += item.count_data;
                        console.log(
                            `Umur ${item.umurValue}: ${item.count_data} data -> Urea: ${item.setara_urea_rata}, K2SO4: ${item.setara_k2so4}, N: ${item.n}, K: ${item.k}, K/N: ${item.k_n}`
                        );
                    }
                });
                console.log(`Total data yang digunakan: ${totalDataDigunakan} lokasi`);
                console.log("=========================================================");

                console.log("=== DETAIL DATA LOKASI PER KELOMPOK UMUR (BIBIT KECIL) ===");
                Object.keys(detailDataByUmurGroup).sort((a, b) => a - b).forEach((group) => {
                    const items = detailDataByUmurGroup[group];
                    console.log(`Kelompok Umur ${group}: ${items.length} lokasi`);
                    items.forEach((item, index) => {
                        console.log(`  ${index + 1}. ID: ${item.id_lokasi}, Lokasi: ${item.lokasi_nama}, Umur: ${item.umur_decimal}, Urea: ${item.setara_urea}, K2SO4: ${item.setara_k2so4}, Bibit: ${item.bibit} (Jenis: ${item.bibit_jenis}, Kelas: ${item.bibit_kelas})`);
                    });
                });
                console.log("=========================================================");

                // Log bibit errors
                if (bibitErrors.length > 0) {
                    console.log("=== BIBIT ERRORS (Tidak memenuhi kriteria C/K) ===");
                    bibitErrors.slice(0, 10).forEach((error, index) => {
                        console.log(`${index + 1}. ID: ${error.id_lokasi}, Bibit: ${error.bibit} → Jenis: ${error.jenis}, Kelas: ${error.kelas}`);
                    });
                    console.log(`Total errors: ${bibitErrors.length}`);
                    console.log("===================================================");
                }

                console.log("=== CHART DATA (BIBIT KECIL) ===");
                console.log("xLabels:", xLabels);
                console.log("Standard SBT Data (uData):", uData);
                console.log("Setara Urea Data (pData):", pData);
                console.log("================================");
            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Hitung nilai maksimum untuk menentukan yAxis
    const getMaxChartValue = () => {
        if (chartData.uData.length === 0 && chartData.pData.length === 0) return 0;

        const maxUData = Math.max(...chartData.uData);
        const maxPData = Math.max(...chartData.pData);
        return Math.max(maxUData, maxPData);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini</Alert>
            </Box>
        );
    }

    // Hitung ticks untuk yAxis
    const maxChartValue = getMaxChartValue();
    const yAxisTicks = getYAxisTicks(maxChartValue);

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Akumulasi Urea Bibit Kecil (Kg/Ha)
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip informasi filter yang digunakan */}
                        {/*<Chip label="Status: NFFC" size="small" color="primary" variant="outlined" />
                        <Chip label="Jenis Bibit: C" size="small" color="warning" variant="outlined" />
                        <Chip label="Kelas Bibit: K" size="small" color="error" variant="outlined" />*/}
                        <Chip 
                            label="Detail Lokasi" 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                            onClick={handleOpenDetailDialog}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Stack>
                </Stack>

                {/* Tabs untuk Line Chart dan Tabel */}
                <Box sx={{ mb: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none", // Menjaga teks chip tetap sesuai case aslinya
                                "&:hover": {
                                    backgroundColor: "transparent", // Menghapus highlight kotak saat hover tab
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple // 1. Matikan ripple pada area kotak Tab
                            label={
                                <Chip
                                    label="Line Chart"
                                    size="small"
                                    onClick={() => {}} // 2. Aktifkan ripple hanya di dalam Chip
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple // 1. Matikan ripple pada area kotak Tab
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}} // 2. Aktifkan ripple hanya di dalam Chip
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {dataList.length > 0 ? (
                    <>
                        {activeTab === 0 ? (
                            // Tampilan Line Chart
                            <Box
                                sx={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: "background.paper",
                                    minHeight: 350,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {chartData.xLabels.length > 0 ? (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: 300,
                                            position: "relative",
                                        }}
                                    >
                                        <LineChart
                                            series={[
                                                {
                                                    data: chartData.pData,
                                                    label: "Setara Urea",
                                                    color: "#2196f3",
                                                    valueFormatter: (value) => value.toString(),
                                                },
                                                {
                                                    data: chartData.uData,
                                                    label: "Standard SBT",
                                                    color: "#ff9800",
                                                    valueFormatter: (value) => value.toString(),
                                                },
                                            ]}
                                            xAxis={[
                                                {
                                                    scaleType: "point",
                                                    data: chartData.xLabels,
                                                    label: "Umur (bulan)",
                                                    disableTicks: false,
                                                    padding: { left: 0, right: 0 },
                                                },
                                            ]}
                                            yAxis={[
                                                {
                                                    min: 0,
                                                    max: Math.max(...yAxisTicks),
                                                    tickInterval: yAxisTicks,
                                                    valueFormatter: (value) => value.toString(),
                                                    disableLine: false,
                                                    disableTicks: false,
                                                },
                                            ]}
                                            margin={{ top: 20, right: 10, bottom: 20, left: 0 }}
                                            slotProps={{
                                                legend: {
                                                    direction: "row",
                                                    position: { vertical: "top", horizontal: "right" },
                                                    padding: 0,
                                                    itemMarkWidth: 8,
                                                    itemMarkHeight: 8,
                                                    labelStyle: { fontSize: 11 },
                                                    itemGap: 8,
                                                },
                                            }}
                                            height={300}
                                            disableClipPath={true}
                                            sx={{
                                                "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                                                    stroke: "#666666",
                                                },
                                                "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                                                    stroke: "#666666",
                                                },
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body1" color="text.secondary">
                                        Tidak ada data untuk ditampilkan dalam chart.
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            // Tampilan Tabel (dengan perubahan: hilangkan kolom Jumlah Data dan Detail)
                            <>
                                {/* Gunakan CustomTableContainer di sini */}
                                <CustomTableContainer
                                    sx={{
                                        maxHeight: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                    }}
                                    hoverScrollbar={true}
                                    scrollbarHeight="8px"
                                    scrollbarWidth="8px"
                                    scrollbarThumbColor="#c1c1c1"
                                    scrollbarHoverColor="#a8a8a8"
                                >
                                    <Table stickyHeader aria-label="akumulasi urea bibit kecil table" size="small">
                                        <TableHead>
                                            <TableRow className="tr">
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Umur (bulan)
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Setara Urea
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    N
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Setara K2SO4
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    K
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    K/N
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Standard SBT
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {dataList.map((row, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {row.umur}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <span
                                                            style={{
                                                                color: row.count_data > 0 ? "inherit" : "#999",
                                                                fontWeight: row.count_data > 0 ? "normal" : "normal",
                                                            }}
                                                        >
                                                            {row.setara_urea_rata}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <span
                                                            style={{
                                                                color: row.n !== "-" ? "inherit" : "#999",
                                                                fontWeight: row.n !== "-" ? "normal" : "normal",
                                                            }}
                                                        >
                                                            {row.n}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <span
                                                            style={{
                                                                color: row.count_data > 0 ? "inherit" : "#999",
                                                                fontWeight: row.count_data > 0 ? "normal" : "normal",
                                                            }}
                                                        >
                                                            {row.setara_k2so4}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <span
                                                            style={{
                                                                color: row.k !== "-" ? "inherit" : "#999",
                                                                fontWeight: row.k !== "-" ? "normal" : "normal",
                                                            }}
                                                        >
                                                            {row.k}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <span
                                                            style={{
                                                                color: row.k_n !== "-" ? "inherit" : "#999",
                                                                fontWeight: row.k_n !== "-" ? "normal" : "normal",
                                                            }}
                                                        >
                                                            {row.k_n}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {row.standard_sbt}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CustomTableContainer>
                            </>
                        )}
                    </>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 200,
                            border: "1px dashed #e0e0e0",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Tidak ada data Akumulasi Urea Bibit Kecil. Pastikan data Standar Per Umur dengan Jenis
                            Bibit=C, Kelas Bibit=K, dan Standar SBT tidak kosong sudah tersedia.
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Dialog Full Screen untuk Detail Lokasi */}
            <Dialog
                fullScreen
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="span">
                                Detail Lokasi - Bibit Kecil
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Total: {debugInfo.finalDataCount} lokasi yang memenuhi kriteria
                            </Typography>
                        </Box>
                        {/* Search box dipindahkan ke header */}
                        <TextField
                            size="small"
                            placeholder="Cari detail lokasi..."
                            value={detailSearchQuery}
                            onChange={(e) => setDetailSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                width: 300,
                                mr: 2,
                                '& .MuiOutlinedInput-root': {
                                    height: 40,
                                }
                            }}
                        />
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleCloseDetailDialog}
                            aria-label="close"
                            sx={{ ml: 1 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {/* Detail table */}
                    <Paper sx={{ mt: 3, boxShadow:0 }}>
                        <DetailLokasiTable 
                            detailData={detailData} 
                            detailSearchQuery={detailSearchQuery} 
                        />
                    </Paper>
                </DialogContent>
                {/* Hilangkan DialogActions (footer dengan tombol Tutup) */}
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AkumUreaBibitKecil;